import axiosInstance from './axiosConfig';

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

export const isPushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

const isNotifySupported = () =>
  typeof window !== 'undefined' && 'Notification' in window;

// Per-session, in-memory baseline of the inbox total per account. Only mail that
// arrives while the app is running triggers a local notification (no stale
// cross-session catch-up, no duplicates with server push).
const lastInboxTotals = {};

/**
 * Detect newly-arrived inbox mail from the periodic counts poll and show a
 * local desktop notification. Covers EXTERNAL mail (e.g. Gmail) that never
 * passes through the app's send path, as long as the app/tab is running.
 */
export const notifyOnNewMail = async (counts, userEmail) => {
  if (!isNotifySupported() || Notification.permission !== 'granted') return;

  const total = counts?.inboxTotal;
  if (typeof total !== 'number') return;

  const key = userEmail || 'anon';
  const prev = lastInboxTotals[key];
  lastInboxTotals[key] = total;

  if (typeof prev !== 'number') return; // first poll = baseline
  const diff = total - prev;
  if (diff <= 0) return;

  // Don't interrupt while the user is actively looking at the app.
  if (typeof document !== 'undefined' && document.hasFocus()) return;

  const title = diff === 1 ? 'New mail' : `${diff} new messages`;
  const options = {
    body: diff === 1 ? 'You have a new message' : `You have ${diff} new messages`,
    icon: '/pwa-icon-192.png',
    badge: '/pwa-icon-192.png',
    tag: 'atozasmail-newmail',
    renotify: true,
    data: { url: '/inbox' },
  };

  try {
    const registration = isPushSupported() ? await getRegistration() : null;
    if (registration) {
      await registration.showNotification(title, options);
    } else {
      // eslint-disable-next-line no-new
      new Notification(title, options);
    }
  } catch {
    /* ignore */
  }
};

export const getNotificationPermission = () =>
  isPushSupported() ? Notification.permission : 'unsupported';

const getRegistration = async () => {
  if (!isPushSupported()) return null;
  try {
    return await navigator.serviceWorker.ready;
  } catch {
    return null;
  }
};

/**
 * Request permission (if needed) and register this device for push.
 * Returns true on success. Safe to call repeatedly.
 */
export const subscribeToPush = async ({ requestPermission = false } = {}) => {
  if (!isPushSupported()) return false;

  if (Notification.permission === 'denied') return false;
  if (Notification.permission !== 'granted') {
    if (!requestPermission) return false;
    const result = await Notification.requestPermission();
    if (result !== 'granted') return false;
  }

  const registration = await getRegistration();
  if (!registration) return false;

  let keyRes;
  try {
    keyRes = await axiosInstance.get('/api/push/vapid-public-key');
  } catch {
    return false;
  }

  const { enabled, publicKey } = keyRes.data || {};
  if (!enabled || !publicKey) return false;

  try {
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }

    await axiosInstance.post('/api/push/subscribe', {
      subscription: subscription.toJSON(),
    });
    return true;
  } catch (error) {
    console.warn('Push subscription failed:', error?.message || error);
    return false;
  }
};

/**
 * Whether this device currently has an active push subscription.
 */
export const isSubscribed = async () => {
  if (!isPushSupported() || Notification.permission !== 'granted') return false;
  const registration = await getRegistration();
  if (!registration) return false;
  try {
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch {
    return false;
  }
};

/**
 * Remove this device's push subscription (local + server).
 */
export const unsubscribeFromPush = async () => {
  if (!isPushSupported()) return false;
  const registration = await getRegistration();
  if (!registration) return false;

  try {
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return true;

    const { endpoint } = subscription;
    await subscription.unsubscribe().catch(() => {});
    await axiosInstance.post('/api/push/unsubscribe', { endpoint }).catch(() => {});
    return true;
  } catch {
    return false;
  }
};
